import { memo, useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Button, { LinkButton } from '@atlaskit/button/new';
import InlineDialog from '@atlaskit/inline-dialog';
import { Box, Text, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import {
  Allowance,
  Capabilities,
  Repository,
} from 'src/components/pipelines/models';
import { PIPELINES_MARKETING_FREE_MINUTES_ALLOWANCE } from 'src/components/pipelines/resources/allowance';
import { fetchData } from 'src/components/pipelines/resources/utils';
import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { useConfigBoolean } from 'src/contexts/statsig-config-provider';
import { StatsigFeatureKeys } from 'src/features/features';
import { Link } from 'src/router/components/link';
import { useParams } from 'src/router/hooks';
import { getIsWorkspaceAdmin } from 'src/selectors/global-selectors';

import PremiumIcon from '../assets/PremiumIcon';
import { formatDate } from '../utils';
import { getAdminPlans, getPlansPageURL } from '../utils/links';

import {
  UsageContainer,
  UsageDescription,
  UsageDialogWrapper,
  UsageHeader,
  UsageHr,
  UsageLimitedInfo,
  UsageTitle,
  UsageWrapper,
} from './styled';
import { messages } from './Usage.i18n';

export type Props = {
  allowance: Allowance;
  capabilities: Capabilities;
  repository: Repository;
  _isDialogOpen?: boolean;
  isVrTest?: boolean; // temporary fix to VR test for marketing campaign
};

const notificationStyles = xcss({
  display: 'block',
  width: '8px',
  height: '8px',
  backgroundColor: 'color.background.discovery.bold',
  position: 'absolute',
  top: '-2px',
  right: '-2px',
  borderRadius: token('radius.full'),
  border: `3px solid ${token('color.background.discovery.bold')}`,
});

const UsageNotification = () => {
  return <Box xcss={notificationStyles} />;
};

const Usage = ({
  allowance,
  capabilities,
  repository,
  _isDialogOpen,
  isVrTest = false,
}: Props) => {
  const { formatMessage } = useIntl();

  const { repositoryFullSlug } = useParams<{ repositoryFullSlug: string }>();
  const [isDialogOpen, setIsDialogOpen] = useState(_isDialogOpen || false);
  const [isMarketingBannerEnabled, setIsMarketingBannerEnabled] =
    useState(false);

  const isWorkspaceAdmin = useSelector(getIsWorkspaceAdmin);
  const toggleShowingDialog = useCallback(() => {
    setIsDialogOpen(!isDialogOpen);
  }, [setIsDialogOpen, isDialogOpen]);

  const onCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, [setIsDialogOpen]);

  useEffect(() => {
    async function fetchMarketingBannerEnabled() {
      const { enabled } = await fetchData(
        `${envBaseUrl(
          '/!api/internal'
        )}/repositories/${repositoryFullSlug}/pipelines-config/free-minutes-enabled`,
        'pipelines-free-minutes-campaign-exception'
      );
      if (enabled) setIsMarketingBannerEnabled(true);
    }
    if (!isVrTest) fetchMarketingBannerEnabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPipelinesFreeMinutesEnabled = useConfigBoolean(
    StatsigFeatureKeys.isPipelinesFreeMinutesEnabled,
    false
  );

  return (
    <InlineDialog
      content={
        <UsageDialogWrapper>
          <UsageWrapper>
            <UsageTitle>{formatMessage(messages.usageTitle)}</UsageTitle>
            {!allowance.hasFetched || !capabilities.hasFetchedCapabilities ? (
              <Spinner size="small" />
            ) : (
              <div>
                {capabilities.limited ? (
                  <>
                    <p>
                      <FormattedMessage
                        {...messages.includedMinutesUsed}
                        values={{
                          usage: `${allowance.totalUsage.toLocaleString()} / ${allowance.quota.toLocaleString()}`,
                          bold: chunks => <strong>{chunks}</strong>,
                        }}
                      />
                    </p>
                    {isPipelinesFreeMinutesEnabled &&
                      isMarketingBannerEnabled &&
                      allowance.freeUsage !== null && (
                        <Text as="p">
                          <FormattedMessage
                            {...messages.limitedFreeMinutesUsed}
                            values={{
                              usage: `${allowance.freeUsage.toLocaleString()} / ${PIPELINES_MARKETING_FREE_MINUTES_ALLOWANCE}`,
                              bold: chunks => (
                                <Text weight="bold">{chunks}</Text>
                              ),
                            }}
                          />
                        </Text>
                      )}
                  </>
                ) : (
                  <>
                    <p>
                      <FormattedMessage
                        {...messages.minutesUsed}
                        values={{
                          usage: allowance.totalUsage.toLocaleString(),
                          bold: chunks => <strong>{chunks}</strong>,
                        }}
                      />
                    </p>
                    {isPipelinesFreeMinutesEnabled &&
                      isMarketingBannerEnabled &&
                      allowance.freeUsage !== null && (
                        <Text as="p">
                          <FormattedMessage
                            {...messages.freeMinutesUsed}
                            values={{
                              usage: allowance.freeUsage.toLocaleString(),
                              freeAllowance:
                                PIPELINES_MARKETING_FREE_MINUTES_ALLOWANCE,
                              bold: chunks => (
                                <Text weight="bold">{chunks}</Text>
                              ),
                            }}
                          />
                        </Text>
                      )}
                  </>
                )}
                {!allowance.isErrored && (
                  <span>
                    <FormattedMessage
                      {...messages.usageResetDate}
                      values={{
                        resetDate: formatDate(
                          allowance.endDate,
                          'date-us-short'
                        ),
                      }}
                    />
                  </span>
                )}
                {capabilities.limited && (
                  <UsageLimitedInfo>
                    <span>{formatMessage(messages.usageLimitedInfo)}</span>
                  </UsageLimitedInfo>
                )}
                {isWorkspaceAdmin ? (
                  <p>
                    <Link href={getPlansPageURL(repository.path)}>
                      {formatMessage(messages.viewPlanDetails)}
                    </Link>
                  </p>
                ) : null}
              </div>
            )}
          </UsageWrapper>
          {!capabilities.isPremium ? (
            <>
              <UsageHr />
              <UsageContainer>
                <UsageHeader>
                  <PremiumIcon />
                  <span>{formatMessage(messages.needMoreMinutes)}</span>
                </UsageHeader>
                <UsageDescription>
                  {formatMessage(messages.upgradeDescription)}
                </UsageDescription>
                <LinkButton
                  appearance="primary"
                  href={getAdminPlans(
                    repository.path?.split('/').length
                      ? repository.path.split('/')[0]
                      : undefined
                  )}
                  target="_top"
                  rel="nofollow"
                >
                  {formatMessage(messages.upgradeButton)}
                </LinkButton>
              </UsageContainer>
            </>
          ) : null}
        </UsageDialogWrapper>
      }
      onClose={onCloseDialog}
      isOpen={isDialogOpen}
      placement="bottom-end"
    >
      <Button onClick={toggleShowingDialog}>
        {formatMessage(messages.usageButton)}
        {isPipelinesFreeMinutesEnabled && isMarketingBannerEnabled && (
          <UsageNotification />
        )}
      </Button>
    </InlineDialog>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(Usage);
